import { useState, useEffect, useCallback, useRef, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { 
  Settings, RefreshCw, Plus, Trash2, TestTube, Check, X, Server, Key, Cpu,
  Eye, FileUp, Video, ChevronDown, ChevronRight,
  AlertCircle, CheckCircle2, Link2, Edit3, Power, PowerOff, Layers, Type, Save,
  MessageSquare, User, LogIn, LogOut, Copy, ExternalLink, Download, Upload,
  MoreVertical, Search
} from 'lucide-react';
import toast from 'react-hot-toast';
import { formatDateTime } from '../../utils/dateFormat';
import { formatCompactNumber } from '../../utils/formatNumber';
import axios from 'axios';
import PageTitle from '../../components/PageTitle';

// Generate a unique ID (with fallback for older browsers)
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for browsers without crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

interface PublicModel {
  uuid: string;
  id: string;
  provider: 'github-copilot';
  enabled: boolean;
  copilotModel?: string;
  reasoningEffort?: string;
  minTier?: string;
  contextWindow?: number;
}

interface CopilotModelOption {
  id: string;
  name: string;
  vendor?: string;
  category?: string;
  priceCategory?: string;
  inputPrice?: number;
  outputPrice?: number;
  cachePrice?: number;
  contextWindow?: number;
  maxOutputTokens?: number;
  supportsVision?: boolean;
  supportsTools?: boolean;
  reasoningEfforts?: string[];
  preview?: boolean;
  endpoints?: string[];
}

interface AISettings {
  publicModels?: PublicModel[];
  priorityQueue?: {
    enabled?: boolean;
    maxConcurrent?: number;
  };
  forceFallback?: boolean;
}

interface SystemPrompt {
  id: string;
  name: string;
  content: string;
  modelUuid: string;
  isDefault: boolean;
}

interface SystemParametersData {
  lastUpdated: Date;
  aiSettings?: AISettings;
  systemPrompts: SystemPrompt[];
}

type ModalType = 'addPublicModel' | 'exportConfig' | 'importConfig' | null;

interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  confirmStyle?: 'danger' | 'primary';
  onConfirm: () => void;
}

interface CopilotAccountState {
  authenticated: boolean;
  login?: string;
  name?: string;
  id?: number;
  htmlUrl?: string;
  primaryEmail?: string;
  verifiedPrimaryEmail?: string;
  emailLookupError?: string;
  label?: string;
  source?: string;
  updatedAt?: string;
  reason?: 'not_logged_in' | 'github_lookup_failed';
  error?: string;
}

interface CopilotLoginState {
  deviceCode: string;
  userCode: string;
  verificationUri: string;
  expiresAt: number;
  interval: number;
}

// Helper to format context window
const formatContextWindow = (val: number | undefined) => {
  const size = val || 4096;
  if (size >= 1_000_000) {
    return size % 1_000_000 === 0
      ? `${size / 1_000_000}M`
      : `${(size / 1_000_000).toFixed(1)}M`;
  }
  if (size >= 1_000) return `${Math.round(size / 1_000)}K`;
  return String(size);
};

// Small labelled chip used to render Copilot model metadata.
function MetaChip({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5 text-[11px]"
      title={title}
    >
      <span className="font-medium text-muted-foreground">{label}</span>
      <span className="text-foreground">{value}</span>
    </span>
  );
}

// Format an upstream USD-per-1M-tokens price for display.
const formatTokenPrice = (val: number | undefined) => (typeof val === 'number' ? `$${val.toFixed(2)}` : '—');

// Renders the rich capability metadata for a GitHub Copilot model as chips.
function CopilotModelMetaChips({ model }: { model: CopilotModelOption }) {
  const { t } = useTranslation('dashboard');
  const hasPrice = typeof model.inputPrice === 'number' || typeof model.outputPrice === 'number';
  const priceTitle = [
    `${t('system.price_input')} ${formatTokenPrice(model.inputPrice)}`,
    `${t('system.price_output')} ${formatTokenPrice(model.outputPrice)}`,
    ...(typeof model.cachePrice === 'number' ? [`${t('system.price_cache')} ${formatTokenPrice(model.cachePrice)}`] : []),
  ].join(' · ') + ` · ${t('system.price_per_million')}`;
  return (
    <div className="flex flex-wrap gap-1.5">
      {model.vendor && <MetaChip label={t('system.cap_vendor')} value={model.vendor} />}
      {model.category && <MetaChip label={t('system.cap_category')} value={model.category} />}
      {hasPrice ? (
        <MetaChip
          label={t('system.cap_price')}
          value={`${formatTokenPrice(model.inputPrice)} / ${formatTokenPrice(model.outputPrice)} ${t('system.price_per_million')}`}
          title={priceTitle}
        />
      ) : model.priceCategory ? (
        <MetaChip label={t('system.cap_price')} value={model.priceCategory} />
      ) : null}
      {typeof model.contextWindow === 'number' && (
        <MetaChip label={t('system.cap_context')} value={formatContextWindow(model.contextWindow)} />
      )}
      {typeof model.maxOutputTokens === 'number' && (
        <MetaChip label={t('system.cap_max_output')} value={formatContextWindow(model.maxOutputTokens)} />
      )}
      {model.reasoningEfforts && model.reasoningEfforts.length > 0 && (
        <MetaChip label={t('system.cap_reasoning')} value={model.reasoningEfforts.join(' / ')} />
      )}
      {model.endpoints && model.endpoints.length > 0 && (
        <MetaChip label={t('system.cap_endpoints')} value={model.endpoints.join(', ')} />
      )}
      {model.supportsVision && (
        <span className="inline-flex items-center gap-1 rounded-md border border-emerald-300/50 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
          <Eye className="h-3 w-3" />{t('system.cap_vision')}
        </span>
      )}
      {model.supportsTools && (
        <span className="inline-flex items-center gap-1 rounded-md border border-blue-300/50 bg-blue-50 px-2 py-0.5 text-[11px] text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
          <Cpu className="h-3 w-3" />{t('system.cap_tools')}
        </span>
      )}
      {model.preview && (
        <span className="inline-flex items-center rounded-md border border-amber-300/50 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
          {t('system.cap_preview')}
        </span>
      )}
    </div>
  );
}

function DashboardSystemParameters() {
  const { t } = useTranslation('dashboard');
  const [data, setData] = useState<SystemParametersData>({
    lastUpdated: new Date(),
    aiSettings: {},
    systemPrompts: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Active tab for settings sections
  const [activeSection, setActiveSection] = useState<'copilot' | 'public-models' | 'system-prompts'>('copilot');

  // Selected system prompt in list-detail view
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);

  // GitHub Copilot direct login (device flow)
  const [copilotAccount, setCopilotAccount] = useState<CopilotAccountState | null>(null);
  const [loadingCopilotAccount, setLoadingCopilotAccount] = useState(false);
  const [copilotAccountError, setCopilotAccountError] = useState<string | null>(null);
  const [copilotLogin, setCopilotLogin] = useState<CopilotLoginState | null>(null);
  const [copilotLoginBusy, setCopilotLoginBusy] = useState(false);
  const copilotPollRef = useRef<{ cancelled: boolean } | null>(null);
  const [copilotModels, setCopilotModels] = useState<CopilotModelOption[]>([]);
  const [loadingCopilotModels, setLoadingCopilotModels] = useState(false);

  // GitHub Copilot model browser modal (rich card picker for a public model row)
  const [browseModelForUuid, setBrowseModelForUuid] = useState<string | null>(null);
  const [modelSearch, setModelSearch] = useState('');
  const [modelVendorFilter, setModelVendorFilter] = useState('');

  // Add-public-model modal draft
  const [publicModelDraft, setPublicModelDraft] = useState<PublicModel | null>(null);
  const [publicModelTierFilter, setPublicModelTierFilter] = useState('');

  // Modal state
  const [modalType, setModalType] = useState<ModalType>(null);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Config import/export
  const [exportPassphrase, setExportPassphrase] = useState('');
  const [exportPassphraseConfirm, setExportPassphraseConfirm] = useState('');
  const [importPassphrase, setImportPassphrase] = useState('');
  const [importFile, setImportFile] = useState<{ name: string; content: any } | null>(null);
  const [configTransferBusy, setConfigTransferBusy] = useState(false);
  const importFileInputRef = useRef<HTMLInputElement>(null);

  const loadCopilotAccount = useCallback(async (showToast = false) => {
    setLoadingCopilotAccount(true);
    setCopilotAccountError(null);
    try {
      const response = await axios.get('/api/admin/system-parameters/copilot/account');
      setCopilotAccount(response.data);
      if (showToast) toast.success(t('system.copilot_account_refreshed'));
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || t('system.copilot_load_failed');
      setCopilotAccountError(message);
      if (showToast) toast.error(message);
    } finally {
      setLoadingCopilotAccount(false);
    }
  }, [t]);

  const loadCopilotModels = useCallback(async (showToast = false, forceRefresh = false) => {
    setLoadingCopilotModels(true);
    try {
      const response = await axios.get('/api/admin/system-parameters/copilot/models', {
        params: forceRefresh ? { refresh: 'true' } : undefined,
      });
      const models: CopilotModelOption[] = response.data.models || [];
      setCopilotModels(models);
      if (showToast) toast.success(t('system.copilot_models_refreshed', { count: models.length }));
    } catch (error: any) {
      if (showToast) toast.error(error.response?.data?.error || error.message || t('system.copilot_models_load_failed'));
    } finally {
      setLoadingCopilotModels(false);
    }
  }, [t]);

  const cancelCopilotLogin = useCallback(() => {
    if (copilotPollRef.current) copilotPollRef.current.cancelled = true;
    copilotPollRef.current = null;
    setCopilotLogin(null);
    setCopilotLoginBusy(false);
  }, []);

  const startCopilotLogin = useCallback(async () => {
    if (copilotPollRef.current) copilotPollRef.current.cancelled = true;
    setCopilotLoginBusy(true);
    setCopilotAccountError(null);
    try {
      const response = await axios.post('/api/admin/system-parameters/copilot/login/start');
      const start = response.data as CopilotLoginState & { expiresIn: number };
      const login: CopilotLoginState = {
        deviceCode: start.deviceCode,
        userCode: start.userCode,
        verificationUri: start.verificationUri,
        expiresAt: Date.now() + start.expiresIn * 1000,
        interval: start.interval,
      };
      setCopilotLogin(login);

      const controller = { cancelled: false };
      copilotPollRef.current = controller;

      const poll = async () => {
        if (controller.cancelled) return;
        if (Date.now() > login.expiresAt) {
          copilotPollRef.current = null;
          setCopilotLogin(null);
          setCopilotLoginBusy(false);
          toast.error(t('system.copilot_login_expired'));
          return;
        }
        try {
          const pollResponse = await axios.post('/api/admin/system-parameters/copilot/login/poll', {
            deviceCode: login.deviceCode,
          });
          if (controller.cancelled) return;
          const result = pollResponse.data;
          if (result.status === 'authorized') {
            copilotPollRef.current = null;
            setCopilotLogin(null);
            setCopilotLoginBusy(false);
            setCopilotAccount(result.account);
            toast.success(t('system.copilot_login_success'));
            return;
          }
          if (result.status === 'expired') {
            copilotPollRef.current = null;
            setCopilotLogin(null);
            setCopilotLoginBusy(false);
            toast.error(t('system.copilot_login_expired'));
            return;
          }
          if (result.status === 'denied') {
            copilotPollRef.current = null;
            setCopilotLogin(null);
            setCopilotLoginBusy(false);
            toast.error(t('system.copilot_login_denied'));
            return;
          }
          if (result.status === 'error') {
            copilotPollRef.current = null;
            setCopilotLogin(null);
            setCopilotLoginBusy(false);
            toast.error(result.error || t('system.copilot_login_failed'));
            return;
          }
          const nextInterval = (typeof result.interval === 'number' ? result.interval : login.interval) * 1000;
          window.setTimeout(poll, nextInterval);
        } catch (error: any) {
          if (controller.cancelled) return;
          copilotPollRef.current = null;
          setCopilotLogin(null);
          setCopilotLoginBusy(false);
          toast.error(error.response?.data?.error || error.message || t('system.copilot_login_failed'));
        }
      };

      window.setTimeout(poll, login.interval * 1000);
    } catch (error: any) {
      copilotPollRef.current = null;
      setCopilotLoginBusy(false);
      toast.error(error.response?.data?.error || error.message || t('system.copilot_login_failed'));
    }
  }, [t]);

  const handleCopilotLogout = useCallback(() => {
    setConfirmDialog({
      isOpen: true,
      title: t('system.copilot_logout_confirm_title'),
      message: t('system.copilot_logout_confirm_message'),
      confirmText: t('system.copilot_logout'),
      confirmStyle: 'danger',
      onConfirm: async () => {
        try {
          await axios.post('/api/admin/system-parameters/copilot/logout');
          setCopilotAccount({ authenticated: false, reason: 'not_logged_in' });
          toast.success(t('system.copilot_logout_success'));
        } catch (error: any) {
          toast.error(error.response?.data?.error || error.message || t('system.copilot_logout_failed'));
        } finally {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        }
      },
    });
  }, [t]);

  const handleCopyCopilotCode = useCallback((code: string) => {
    navigator.clipboard.writeText(code).then(
      () => toast.success(t('system.copilot_login_code_copied')),
      () => toast.error(t('system.copilot_login_code_copy_failed')),
    );
  }, [t]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeSection === 'copilot') {
      loadCopilotAccount();
    }
  }, [activeSection, loadCopilotAccount]);

  useEffect(() => {
    if (activeSection === 'public-models' && copilotModels.length === 0) {
      loadCopilotModels();
    }
  }, [activeSection, copilotModels.length, loadCopilotModels]);

  useEffect(() => () => {
    if (copilotPollRef.current) copilotPollRef.current.cancelled = true;
    copilotPollRef.current = null;
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/system-parameters');
      setData({
        lastUpdated: new Date(response.data.lastUpdated),
        aiSettings: response.data.aiSettings || {},
        systemPrompts: response.data.systemPrompts || [],
      });
    } catch (error: any) {
      console.error('Failed to load system parameters:', error);
      toast.error(error.response?.data?.error || t('system.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const openExportConfig = () => {
    setExportPassphrase('');
    setExportPassphraseConfirm('');
    setModalType('exportConfig');
  };

  const handleExportConfig = async () => {
    if (!exportPassphrase) {
      toast.error(t('system.passphrase_required'));
      return;
    }
    if (exportPassphrase !== exportPassphraseConfirm) {
      toast.error(t('system.export_passphrase_mismatch'));
      return;
    }
    setConfigTransferBusy(true);
    try {
      const response = await axios.post('/api/admin/system-parameters/export', { passphrase: exportPassphrase });
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `etherworks-system-parameters-${ts}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast.success(t('system.export_success'));
      setModalType(null);
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('system.export_failed'));
    } finally {
      setConfigTransferBusy(false);
    }
  };

  const handleImportFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const content = JSON.parse(String(reader.result));
        setImportFile({ name: file.name, content });
        setImportPassphrase('');
        setModalType('importConfig');
      } catch {
        toast.error(t('system.import_invalid_file'));
      }
    };
    reader.onerror = () => toast.error(t('system.import_invalid_file'));
    reader.readAsText(file);
  };

  const handleImportConfig = async () => {
    if (!importFile) return;
    if (!importPassphrase) {
      toast.error(t('system.passphrase_required'));
      return;
    }
    setConfigTransferBusy(true);
    try {
      const response = await axios.post('/api/admin/system-parameters/import', {
        passphrase: importPassphrase,
        file: importFile.content,
      });
      toast.success(t('system.import_success'));
      setModalType(null);
      setImportFile(null);
      await loadData();
      await loadCopilotAccount();
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('system.import_failed'));
    } finally {
      setConfigTransferBusy(false);
    }
  };

  // Save system prompts
  const saveSystemPrompts = useCallback(async (prompts: SystemPrompt[]) => {
    try {
      setSaving(true);
      await axios.put('/api/admin/system-parameters', { systemPrompts: prompts });
      setData(prev => ({ ...prev, systemPrompts: prompts }));
      toast.success(t('system.prompt_saved'));
    } catch (error: any) {
      console.error('Failed to save system prompts:', error);
      toast.error(error.response?.data?.error || t('system.prompt_save_failed'));
    } finally {
      setSaving(false);
    }
  }, []);

  const saveAISettings = useCallback(async (aiSettings: AISettings) => {
    try {
      setSaving(true);
      const response = await axios.put('/api/admin/system-parameters', { aiSettings });
      setData(prev => ({
        ...prev,
        aiSettings: response.data.aiSettings || aiSettings,
        lastUpdated: new Date(response.data.lastUpdated),
      }));
      toast.success(t('system.ai_settings_saved'));
    } catch (error: any) {
      console.error('Failed to save AI settings:', error);
      toast.error(error.response?.data?.error || t('system.ai_save_failed'));
    } finally {
      setSaving(false);
    }
  }, [t]);

  const updateAISettings = (patch: AISettings) => {
    setData(prev => ({
      ...prev,
      aiSettings: {
        publicModels: [],
        ...(prev.aiSettings || {}),
        ...patch,
      },
    }));
  };

  const updatePublicModels = (publicModels: PublicModel[]) => {
    updateAISettings({ publicModels });
  };

  const persistPublicModels = (publicModels: PublicModel[]) => {
    saveAISettings({
      publicModels,
      priorityQueue: data.aiSettings?.priorityQueue,
    });
  };

  const handleAddPublicModel = () => {
    setPublicModelDraft({
      uuid: generateUUID(),
      id: '',
      provider: 'github-copilot',
      enabled: true,
      copilotModel: '',
      reasoningEffort: '',
      minTier: 'free',
    });
    setModalType('addPublicModel');
  };

  const handleEditPublicModel = (model: PublicModel) => {
    setPublicModelDraft({ ...model });
    setModalType('addPublicModel');
  };

  const handleSavePublicModelDraft = () => {
    if (!publicModelDraft) return;
    const draft: PublicModel = {
      ...publicModelDraft,
      id: publicModelDraft.id.trim(),
    };
    // Persist the upstream context window for Copilot models so the customer
    // model list can show it.
    const meta = copilotModels.find(m => m.id === (draft.copilotModel || '').trim());
    draft.contextWindow = meta?.contextWindow ?? draft.contextWindow;
    if (!draft.id) {
      toast.error(t('system.public_model_id_required'));
      return;
    }
    const existingModels = data.aiSettings?.publicModels || [];
    const isEdit = existingModels.some(model => model.uuid === draft.uuid);
    if (existingModels.some(model => model.id === draft.id && model.uuid !== draft.uuid)) {
      toast.error(t('system.public_model_id_duplicate'));
      return;
    }
    const nextModels = isEdit
      ? existingModels.map(model => (model.uuid === draft.uuid ? draft : model))
      : [...existingModels, draft];
    persistPublicModels(nextModels);
    setModalType(null);
    setPublicModelDraft(null);
  };

  const handleUpdatePublicModel = (uuid: string, patch: Partial<PublicModel>) => {
    const nextModels = (data.aiSettings?.publicModels || []).map(model => (
      model.uuid === uuid ? { ...model, ...patch } : model
    ));
    updatePublicModels(nextModels);
  };

  const handleTogglePublicModel = (uuid: string) => {
    const nextModels = (data.aiSettings?.publicModels || []).map(model => (
      model.uuid === uuid ? { ...model, enabled: !model.enabled } : model
    ));
    persistPublicModels(nextModels);
  };

  // Apply a Copilot model pick to either an existing row or the add-modal draft
  const applyCopilotSelection = (uuid: string, copilotModel: string) => {
    if (publicModelDraft && publicModelDraft.uuid === uuid) {
      setPublicModelDraft(prev => {
        if (!prev) return prev;
        const next: PublicModel = { ...prev, copilotModel };
        const currentId = prev.id.trim();
        const previousAutoId = prev.copilotModel ? `ether-${prev.copilotModel}` : '';
        if (!currentId || currentId === previousAutoId) {
          next.id = `ether-${copilotModel}`;
        }
        return next;
      });
    } else {
      handleUpdatePublicModel(uuid, { copilotModel });
    }
  };

  const updatePublicModelDraft = (patch: Partial<PublicModel>) => {
    setPublicModelDraft(prev => (prev ? { ...prev, ...patch } : prev));
  };

  const handleDeletePublicModel = (uuid: string) => {
    const promptReferences = data.systemPrompts
      .filter(prompt => prompt.modelUuid === uuid)
      .map(prompt => prompt.name || t('system.untitled_prompt'));
    if (promptReferences.length > 0) {
      toast.error(t('system.public_model_prompt_reference_blocked', { references: promptReferences.join(', ') }));
      return;
    }

    persistPublicModels((data.aiSettings?.publicModels || []).filter(model => model.uuid !== uuid));
  };

  if (loading) {
    return (
        <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-3">
          <RefreshCw className="h-5 w-5 animate-spin text-primary" />
          <span className="text-muted-foreground">{t('common:loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageTitle titleKey="system.title" namespace="dashboard" descriptionKey="system.subtitle" />
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">
            {t('system.title')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('system.subtitle')}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {saving && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span className="text-sm">{t('common:saving')}</span>
            </div>
          )}
          <input
            ref={importFileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={handleImportFileSelected}
          />
          <button
            onClick={openExportConfig}
            className="px-4 py-2 border border-border bg-card hover:bg-muted text-foreground rounded-xl flex items-center gap-2 font-medium transition-all shadow-sm"
          >
            <Download className="h-4 w-4" />
            {t('system.export_config')}
          </button>
          <button
            onClick={() => importFileInputRef.current?.click()}
            className="px-4 py-2 border border-border bg-card hover:bg-muted text-foreground rounded-xl flex items-center gap-2 font-medium transition-all shadow-sm"
          >
            <Upload className="h-4 w-4" />
            {t('system.import_config')}
          </button>
          {activeSection === 'public-models' && (
            <button
              onClick={handleAddPublicModel}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl flex items-center gap-2 font-medium transition-all shadow-sm"
            >
              <Plus className="h-4 w-4" />
              {t('system.add_public_model')}
            </button>
          )}
          {activeSection === 'system-prompts' && (
            <button
              onClick={() => {
                const newPrompt: SystemPrompt = {
                  id: generateUUID(),
                  name: '',
                  content: '',
                  modelUuid: '',
                  isDefault: false,
                };
                setData(prev => ({ ...prev, systemPrompts: [...prev.systemPrompts, newPrompt] }));
                setSelectedPromptId(newPrompt.id);
              }}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl flex items-center gap-2 font-medium transition-all shadow-sm"
            >
              <Plus className="h-4 w-4" />
              {t('system.add_prompt')}
            </button>
          )}
        </div>
      </div>

      {/* Section Tabs */}
      <div className="w-full overflow-x-auto pb-1">
        <div className="flex gap-1 p-1 bg-muted rounded-xl w-max min-w-full sm:min-w-0">
          <button
            onClick={() => setActiveSection('copilot')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
              activeSection === 'copilot'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Link2 className="h-4 w-4" />
            {t('system.copilot_tab')}
          </button>
          <button
            onClick={() => setActiveSection('public-models')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
              activeSection === 'public-models'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Layers className="h-4 w-4" />
            {t('system.public_models_tab')}
          </button>
          <button
            onClick={() => setActiveSection('system-prompts')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
              activeSection === 'system-prompts'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <MessageSquare className="h-4 w-4" />
            {t('system.system_prompts')}
          </button>
        </div>
      </div>

      {activeSection === 'copilot' && (() => {
        return (
          <div className="bg-card rounded-xl border border-border p-5 space-y-5">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-xl">
                  <Link2 className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-foreground">{t('system.routing_title')}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{t('system.routing_desc')}</p>
                </div>
              </div>
            </div>

            <div className="pt-5 border-t border-border space-y-3">
              <div className="p-4 rounded-xl bg-muted/50 space-y-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={data.aiSettings?.forceFallback ?? false}
                    onChange={e => {
                      const forceFallback = e.target.checked;
                      updateAISettings({ forceFallback });
                      saveAISettings({
                        publicModels: data.aiSettings?.publicModels || [],
                        priorityQueue: data.aiSettings?.priorityQueue,
                        forceFallback,
                      });
                    }}
                    className="rounded border-border"
                  />
                  <span className="text-sm font-medium text-foreground">{t('system.force_fallback')}</span>
                </label>
                <p className="text-xs text-muted-foreground">{t('system.force_fallback_desc')}</p>
              </div>
            </div>

            <div className="pt-5 border-t border-border space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-xl shrink-0">
                  <Layers className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">{t('system.priority_queue_title')}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{t('system.priority_queue_desc')}</p>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-muted/50 space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={data.aiSettings?.priorityQueue?.enabled ?? false}
                    onChange={e => updateAISettings({
                      priorityQueue: {
                        ...(data.aiSettings?.priorityQueue || {}),
                        enabled: e.target.checked,
                      },
                    })}
                    className="rounded border-border"
                  />
                  <span className="text-sm font-medium text-foreground">{t('system.priority_queue_enabled')}</span>
                </label>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">{t('system.priority_queue_max_concurrent')}</label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={data.aiSettings?.priorityQueue?.maxConcurrent ?? 0}
                    onChange={e => updateAISettings({
                      priorityQueue: {
                        ...(data.aiSettings?.priorityQueue || {}),
                        maxConcurrent: Math.max(0, Math.floor(Number(e.target.value) || 0)),
                      },
                    })}
                    className="w-full px-3 py-2.5 border border-border rounded-xl bg-background text-foreground text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t('system.priority_queue_max_concurrent_desc')}</p>
                </div>

                <button
                  onClick={() => {
                    saveAISettings({
                      publicModels: data.aiSettings?.publicModels || [],
                      priorityQueue: data.aiSettings?.priorityQueue,
                    });
                  }}
                  disabled={saving}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 flex items-center gap-2 text-sm font-medium transition-colors disabled:opacity-60"
                >
                  <Save className="h-4 w-4" />
                  {t('system.priority_queue_save')}
                </button>
              </div>
            </div>

            <div className="pt-5 border-t border-border space-y-4">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="p-2 bg-primary/10 rounded-xl shrink-0">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-foreground">{t('system.copilot_login_title')}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{t('system.copilot_login_desc')}</p>
                    {copilotAccount?.updatedAt && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('system.copilot_login_updated', { time: formatDateTime(copilotAccount.updatedAt) })}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => loadCopilotAccount(true)}
                    disabled={loadingCopilotAccount}
                    className="px-4 py-2 border border-border rounded-xl hover:bg-muted flex items-center gap-2 text-sm font-medium transition-colors disabled:opacity-60"
                  >
                    <RefreshCw className={`h-4 w-4 ${loadingCopilotAccount ? 'animate-spin' : ''}`} />
                    {t('system.refresh_copilot_account')}
                  </button>
                  {copilotAccount?.authenticated && !copilotLogin && (
                    <button
                      onClick={handleCopilotLogout}
                      className="px-4 py-2 border border-red-500/30 text-red-600 rounded-xl hover:bg-red-500/10 flex items-center gap-2 text-sm font-medium transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      {t('system.copilot_logout')}
                    </button>
                  )}
                </div>
              </div>

              {copilotAccountError && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-2 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span className="break-words">{copilotAccountError}</span>
                </div>
              )}

              {copilotLogin ? (
                <div className="p-4 rounded-xl bg-muted/50 space-y-3">
                  <p className="text-sm text-foreground">{t('system.copilot_login_instruction')}</p>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex items-center gap-2">
                      <code className="px-3 py-2 rounded-lg bg-background border border-border text-lg font-mono tracking-[0.3em] text-foreground">
                        {copilotLogin.userCode}
                      </code>
                      <button
                        onClick={() => handleCopyCopilotCode(copilotLogin.userCode)}
                        className="p-2 border border-border rounded-lg hover:bg-muted transition-colors"
                        title={t('system.copilot_login_copy_code')}
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                    <a
                      href={copilotLogin.verificationUri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl flex items-center gap-2 text-sm font-medium transition-colors w-fit"
                    >
                      <ExternalLink className="h-4 w-4" />
                      {t('system.copilot_login_open')}
                    </a>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 text-sm text-muted-foreground">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      {t('system.copilot_login_waiting')}
                    </span>
                    <button
                      onClick={cancelCopilotLogin}
                      className="px-4 py-2 border border-border rounded-xl hover:bg-muted text-sm font-medium transition-colors"
                    >
                      {t('common:cancel')}
                    </button>
                  </div>
                </div>
              ) : !copilotAccount && loadingCopilotAccount ? (
                <div className="p-4 rounded-xl bg-muted/50 flex items-center gap-2 text-sm text-muted-foreground">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  {t('system.copilot_account_loading')}
                </div>
              ) : copilotAccount?.authenticated ? (
                <div className="p-4 rounded-xl bg-muted/50 min-w-0 space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                    <span className="text-sm font-medium text-foreground break-all">
                      {copilotAccount.label || copilotAccount.login || t('system.copilot_authenticated')}
                    </span>
                  </div>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    {copilotAccount.login && (
                      <p className="break-all">{t('system.copilot_account_login')}: {copilotAccount.login}</p>
                    )}
                    {copilotAccount.verifiedPrimaryEmail && (
                      <p className="break-all">{t('system.copilot_account_email')}: {copilotAccount.verifiedPrimaryEmail}</p>
                    )}
                    {!copilotAccount.verifiedPrimaryEmail && copilotAccount.primaryEmail && (
                      <p className="break-all">{t('system.copilot_account_email')}: {copilotAccount.primaryEmail}</p>
                    )}
                    {typeof copilotAccount.id === 'number' && (
                      <p>{t('system.copilot_account_id')}: {copilotAccount.id}</p>
                    )}
                    {copilotAccount.source && (
                      <p>{t('system.copilot_account_source')}: {copilotAccount.source}</p>
                    )}
                  </div>
                  {copilotAccount.emailLookupError && (
                    <p className="text-xs text-amber-600 break-words">
                      {t('system.copilot_email_lookup_failed', { error: copilotAccount.emailLookupError })}
                    </p>
                  )}
                </div>
              ) : (
                <div className="p-4 rounded-xl bg-muted/50 space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <AlertCircle className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-sm font-medium text-foreground break-all">
                        {copilotAccount?.reason === 'github_lookup_failed' && copilotAccount.login
                          ? copilotAccount.login
                          : t('system.copilot_not_authenticated')}
                      </span>
                    </div>
                    <button
                      onClick={startCopilotLogin}
                      disabled={copilotLoginBusy}
                      className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl flex items-center gap-2 text-sm font-medium transition-colors disabled:opacity-60 w-fit"
                    >
                      {copilotLoginBusy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
                      {t('system.copilot_login_button')}
                    </button>
                  </div>
                  {copilotAccount?.error && (
                    <p className="text-xs text-red-500 break-words">{copilotAccount.error}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {activeSection === 'public-models' && (() => {
        const publicModels = data.aiSettings?.publicModels || [];

        if (publicModels.length === 0) {
          return (
            <div className="bg-card rounded-lg border border-border p-12 text-center">
              <Layers className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">{t('system.no_public_models')}</h3>
              <p className="text-muted-foreground mb-6">{t('system.no_public_models_desc')}</p>
              <button
                onClick={handleAddPublicModel}
                className="px-5 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 font-medium inline-flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                {t('system.add_public_model')}
              </button>
            </div>
          );
        }

        const filteredModels = publicModelTierFilter
          ? publicModels.filter(model => (model.minTier || 'free') === publicModelTierFilter)
          : publicModels;

        return (
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <div className="p-5 border-b border-border flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-foreground">{t('system.public_models_title')}</h2>
                <p className="text-sm text-muted-foreground mt-1">{t('system.public_models_desc')}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <select
                  value={publicModelTierFilter}
                  onChange={e => setPublicModelTierFilter(e.target.value)}
                  className="w-full sm:w-auto px-3 py-2.5 border border-border rounded-xl bg-background text-foreground text-sm"
                >
                  <option value="">{t('system.all_tiers')}</option>
                  <option value="free">{t('system.tier_free')}</option>
                  <option value="plus">{t('system.tier_plus')}</option>
                  <option value="max">{t('system.tier_max')}</option>
                  <option value="ultra">{t('system.tier_ultra')}</option>
                </select>
                <button
                  onClick={() => loadCopilotModels(true, true)}
                  disabled={loadingCopilotModels}
                  className="w-full sm:w-auto px-4 py-2.5 border border-border hover:bg-muted text-foreground rounded-xl flex items-center justify-center gap-2 font-medium transition-all disabled:opacity-60"
                >
                  <RefreshCw className={`h-4 w-4 ${loadingCopilotModels ? 'animate-spin' : ''}`} />
                  {t('system.refresh_copilot_models')}
                  {copilotModels.length > 0 && (
                    <span className="text-xs text-muted-foreground">({copilotModels.length})</span>
                  )}
                </button>
              </div>
            </div>

            {filteredModels.length === 0 ? (
              <div className="p-12 text-center text-sm text-muted-foreground">
                {t('system.no_public_models_in_tier')}
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredModels.map(publicModel => {
                  const tier = publicModel.minTier || 'free';
                  const copilotId = (publicModel.copilotModel || '').trim();
                  const effort = (publicModel.reasoningEffort || '').trim();
                  const effortLabel = effort
                    ? t('system.reasoning_effort_' + (effort === 'xhigh' ? 'max' : effort))
                    : '';
                  const targetSummary = copilotId || t('system.copilot_model_placeholder');
                  return (
                    <div key={publicModel.uuid} className="flex items-center gap-3 sm:gap-4 p-4">
                      <button
                        onClick={() => handleTogglePublicModel(publicModel.uuid)}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                          publicModel.enabled ? 'bg-primary' : 'bg-muted'
                        }`}
                        title={publicModel.enabled ? t('system.disable') : t('system.enable')}
                      >
                        <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                          publicModel.enabled ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-mono font-medium text-foreground truncate">{publicModel.id}</span>
                          <span className="shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary">
                            GitHub Copilot
                          </span>
                          <span className="shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium bg-muted text-muted-foreground">
                            {t('system.tier_' + tier)}
                          </span>
                          {!publicModel.enabled && (
                            <span className="shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium bg-muted text-muted-foreground">
                              {t('common:disabled')}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 truncate font-mono" title={targetSummary}>
                          {targetSummary}{effortLabel ? ` · ${t('system.reasoning_effort')}: ${effortLabel}` : ''}
                        </div>
                        {(() => {
                          const meta = copilotModels.find(m => m.id === copilotId);
                          if (!meta) return null;
                          return <div className="mt-2"><CopilotModelMetaChips model={meta} /></div>;
                        })()}
                      </div>

                      <button
                        onClick={() => handleEditPublicModel(publicModel)}
                        className="shrink-0 p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg"
                        title={t('common:edit')}
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeletePublicModel(publicModel.uuid)}
                        className="shrink-0 p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                        title={t('common:delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* System Prompts Section — List + Detail */}
      {activeSection === 'system-prompts' && (
        data.systemPrompts.length === 0 ? (
          <div className="bg-card rounded-lg border border-border p-12 text-center">
            <MessageSquare className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">{t('system.no_prompts')}</h3>
            <p className="text-muted-foreground mb-6">{t('system.no_prompts_desc')}</p>
            <button
              onClick={() => {
                const newPrompt: SystemPrompt = {
                  id: generateUUID(),
                  name: '',
                  content: '',
                  modelUuid: '',
                  isDefault: false,
                };
                setData(prev => ({ ...prev, systemPrompts: [...prev.systemPrompts, newPrompt] }));
                setSelectedPromptId(newPrompt.id);
              }}
              className="px-5 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 font-medium inline-flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              {t('system.add_prompt')}
            </button>
          </div>
        ) : (() => {
          const allModels = data.aiSettings?.publicModels || [];
          const activePrompt = data.systemPrompts.find(p => p.id === selectedPromptId) || null;
          const activeIdx = activePrompt ? data.systemPrompts.findIndex(p => p.id === selectedPromptId) : -1;

          const getBoundModelName = (modelUuid: string) => {
            if (!modelUuid) return null;
            const found = allModels.find(model => model.uuid === modelUuid);
            return found ? found.id : null;
          };

          return (
            <div className="flex flex-col lg:flex-row bg-card rounded-xl border border-border overflow-hidden min-h-[480px]">
              {/* Left Panel — Prompt List */}
              <div className="w-full lg:w-64 flex-shrink-0 border-b lg:border-b-0 lg:border-r border-border flex flex-col max-h-72 lg:max-h-none">
                <div className="flex-1 overflow-y-auto">
                  {data.systemPrompts.map(prompt => {
                    const isActive = prompt.id === selectedPromptId;
                    const boundModel = getBoundModelName(prompt.modelUuid);
                    return (
                      <button
                        key={prompt.id}
                        onClick={() => setSelectedPromptId(prompt.id)}
                        className={`w-full text-left px-4 py-3 border-b border-border/50 transition-colors ${
                          isActive
                            ? 'bg-primary/10 border-l-2 border-l-primary'
                            : 'hover:bg-muted/50 border-l-2 border-l-transparent'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <MessageSquare className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                          <span className={`text-sm font-medium truncate ${isActive ? 'text-primary' : 'text-foreground'}`}>
                            {prompt.name || t('system.untitled_prompt')}
                          </span>
                        </div>
                        {prompt.isDefault && (
                          <span className="text-[10px] text-primary font-medium mt-0.5 ml-6 block">{t('system.default_prompt')}</span>
                        )}
                        {boundModel && (
                          <span className="text-[11px] text-muted-foreground mt-0.5 ml-6 block truncate">{boundModel}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="p-3 border-t border-border">
                  <button
                    onClick={() => {
                      const newPrompt: SystemPrompt = {
                        id: generateUUID(),
                        name: '',
                        content: '',
                        modelUuid: '',
                        isDefault: false,
                      };
                      setData(prev => ({ ...prev, systemPrompts: [...prev.systemPrompts, newPrompt] }));
                      setSelectedPromptId(newPrompt.id);
                    }}
                    className="w-full px-3 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg flex items-center justify-center gap-2 text-sm font-medium transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    {t('system.add_prompt')}
                  </button>
                </div>
              </div>

              {/* Right Panel — Detail Editor */}
              <div className="flex-1 flex flex-col min-w-0">
                {!activePrompt ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground p-8">
                    <MessageSquare className="h-12 w-12 mb-3 opacity-40" />
                    <p className="text-sm">{t('system.select_prompt')}</p>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col p-6 overflow-y-auto">
                    <div className="space-y-5 flex-1">
                      {/* Prompt Name */}
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex-shrink-0">
                          <MessageSquare className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                        </div>
                        <input
                          type="text"
                          value={activePrompt.name}
                          onChange={e => {
                            const updated = [...data.systemPrompts];
                            updated[activeIdx] = { ...updated[activeIdx], name: e.target.value };
                            setData(prev => ({ ...prev, systemPrompts: updated }));
                          }}
                          placeholder={t('system.prompt_name_placeholder')}
                          className="flex-1 px-3 py-2 border border-border rounded-xl bg-background text-foreground text-sm font-medium placeholder:text-muted-foreground/50"
                        />
                      </div>

                      {/* Prompt Content */}
                      <div className="flex-1 flex flex-col">
                        <label className="block text-sm font-medium text-muted-foreground mb-1.5">
                          {t('system.prompt_content')}
                        </label>
                        <textarea
                          value={activePrompt.content}
                          onChange={e => {
                            const updated = [...data.systemPrompts];
                            updated[activeIdx] = { ...updated[activeIdx], content: e.target.value };
                            setData(prev => ({ ...prev, systemPrompts: updated }));
                          }}
                          placeholder={t('system.prompt_content_placeholder')}
                          rows={12}
                          className="w-full px-3 py-2.5 border border-border rounded-xl bg-background text-foreground text-sm resize-y min-h-[200px] max-h-[60vh] placeholder:text-muted-foreground/50 flex-1"
                        />
                      </div>

                      {/* Default Toggle */}
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => {
                            const updated = data.systemPrompts.map((p, i) => ({
                              ...p,
                              isDefault: i === activeIdx ? !p.isDefault : false,
                            }));
                            setData(prev => ({ ...prev, systemPrompts: updated }));
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            activePrompt.isDefault ? 'bg-primary' : 'bg-muted'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                            activePrompt.isDefault ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                        <div>
                          <p className="text-sm font-medium text-foreground">{t('system.default_prompt')}</p>
                          <p className="text-xs text-muted-foreground">{t('system.default_prompt_desc')}</p>
                        </div>
                      </div>

                      {/* Model Binding */}
                      <div>
                        <label className="block text-sm font-medium text-muted-foreground mb-1.5">
                          {t('system.bind_model')}
                        </label>
                        <p className="text-xs text-muted-foreground mb-2">{t('system.bind_model_desc')}</p>
                        {allModels.length === 0 ? (
                          <p className="text-sm text-muted-foreground italic">{t('system.no_public_models')}</p>
                        ) : (
                          <select
                            value={activePrompt.modelUuid}
                            onChange={e => {
                              const selectedUuid = e.target.value;
                              const updated = data.systemPrompts.map((p, i) => {
                                if (i === activeIdx) return { ...p, modelUuid: selectedUuid };
                                if (selectedUuid && p.modelUuid === selectedUuid) return { ...p, modelUuid: '' };
                                return p;
                              });
                              setData(prev => ({ ...prev, systemPrompts: updated }));
                            }}
                            className="w-full px-3 py-2.5 border border-border rounded-xl bg-background text-foreground text-sm"
                          >
                            <option value="">{t('system.no_model_bound')}</option>
                            {allModels.map(model => (
                              <option key={model.uuid} value={model.uuid}>
                                {model.id}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between pt-5 mt-5 border-t border-border">
                      <button
                        onClick={() => {
                          setConfirmDialog({
                            isOpen: true,
                            title: t('system.delete_prompt'),
                            message: t('system.delete_prompt_confirm'),
                            confirmText: t('common:delete'),
                            confirmStyle: 'danger',
                            onConfirm: () => {
                              const updated = data.systemPrompts.filter(p => p.id !== selectedPromptId);
                              setData(prev => ({ ...prev, systemPrompts: updated }));
                              saveSystemPrompts(updated);
                              setSelectedPromptId(null);
                              setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                            },
                          });
                        }}
                        className="px-4 py-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl flex items-center gap-2 text-sm font-medium transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                        {t('system.delete_prompt')}
                      </button>
                      <button
                        onClick={() => saveSystemPrompts(data.systemPrompts)}
                        className="px-6 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl flex items-center gap-2 font-medium transition-all shadow-sm"
                      >
                        <Save className="h-4 w-4" />
                        {t('system.save_prompts')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()
      )}

      {/* Footer */}
      <div className="text-center text-sm text-muted-foreground">
        {t('system.last_updated')} {formatDateTime(data.lastUpdated)}
      </div>

      {/* Export Config Modal */}
      {modalType === 'exportConfig' && createPortal(
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
          onKeyDown={e => e.key === 'Escape' && setModalType(null)}
        >
          <div className="bg-card rounded-lg shadow-lg w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Download className="h-5 w-5 text-primary" />
                {t('system.export_config_title')}
              </h3>
              <button onClick={() => setModalType(null)} className="p-1 text-muted-foreground hover:text-foreground rounded">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex items-start gap-2 rounded-xl border border-amber-300/50 bg-amber-50 p-3 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{t('system.export_config_warning')}</span>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  {t('system.export_passphrase')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={exportPassphrase}
                  onChange={e => setExportPassphrase(e.target.value)}
                  className="w-full px-3 py-2.5 border border-border rounded-xl bg-background"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  {t('system.export_passphrase_confirm')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={exportPassphraseConfirm}
                  onChange={e => setExportPassphraseConfirm(e.target.value)}
                  className="w-full px-3 py-2.5 border border-border rounded-xl bg-background"
                />
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 p-5 border-t border-border bg-muted/50">
              <button onClick={() => setModalType(null)} className="w-full sm:w-auto px-4 py-2.5 text-muted-foreground hover:bg-muted rounded-xl">
                {t('common:cancel')}
              </button>
              <button
                onClick={handleExportConfig}
                disabled={configTransferBusy}
                className="w-full sm:w-auto px-5 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Download className="h-4 w-4" />
                {t('system.export_config')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Import Config Modal */}
      {modalType === 'importConfig' && importFile && createPortal(
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
          onKeyDown={e => e.key === 'Escape' && setModalType(null)}
        >
          <div className="bg-card rounded-lg shadow-lg w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Upload className="h-5 w-5 text-primary" />
                {t('system.import_config_title')}
              </h3>
              <button onClick={() => setModalType(null)} className="p-1 text-muted-foreground hover:text-foreground rounded">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex items-start gap-2 rounded-xl border border-red-300/50 bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{t('system.import_overwrite_warning')}</span>
              </div>
              <div className="text-sm text-muted-foreground break-all">
                {t('system.import_selected_file')}: <span className="text-foreground">{importFile.name}</span>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  {t('system.import_passphrase')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={importPassphrase}
                  onChange={e => setImportPassphrase(e.target.value)}
                  className="w-full px-3 py-2.5 border border-border rounded-xl bg-background"
                />
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 p-5 border-t border-border bg-muted/50">
              <button onClick={() => setModalType(null)} className="w-full sm:w-auto px-4 py-2.5 text-muted-foreground hover:bg-muted rounded-xl">
                {t('common:cancel')}
              </button>
              <button
                onClick={handleImportConfig}
                disabled={configTransferBusy}
                className="w-full sm:w-auto px-5 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Upload className="h-4 w-4" />
                {t('system.import_config_apply')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Confirmation Dialog */}
      {confirmDialog.isOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
          onKeyDown={e => e.key === 'Escape' && setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
          tabIndex={-1}
        >
          <div className="bg-card rounded-lg shadow-lg w-full max-w-md overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                  confirmDialog.confirmStyle === 'danger' 
                    ? 'bg-red-100 dark:bg-red-900/30' 
                    : 'bg-blue-100 dark:bg-blue-900/30'
                }`}>
                  <AlertCircle className={`h-6 w-6 ${
                    confirmDialog.confirmStyle === 'danger'
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-blue-600 dark:text-blue-400'
                  }`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground">
                    {confirmDialog.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {confirmDialog.message}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex gap-3 justify-end">
                <button
                  onClick={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
                  className="px-4 py-2 text-sm font-medium text-foreground bg-muted hover:bg-muted/80 rounded-lg transition-colors"
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={confirmDialog.onConfirm}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                    confirmDialog.confirmStyle === 'danger'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-primary hover:bg-primary/90'
                  }`}
                >
                  {confirmDialog.confirmText || t('common:confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Add Public Model Modal */}
      {modalType === 'addPublicModel' && publicModelDraft && createPortal(
        (() => {
          const draft = publicModelDraft;
          const isEditPublicModel = (data.aiSettings?.publicModels || []).some(model => model.uuid === draft.uuid);
          const selectedMeta = copilotModels.find(m => m.id === (draft.copilotModel || '').trim());
          const closeModal = () => { setModalType(null); setPublicModelDraft(null); };
          return (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
              onKeyDown={e => e.key === 'Escape' && closeModal()}
            >
              <div className="bg-card rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-border">
                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                    <Layers className="h-5 w-5 text-primary" />
                    {isEditPublicModel ? t('system.edit_public_model_title') : t('system.add_public_model_title')}
                  </h3>
                  <button onClick={closeModal} className="p-1 text-muted-foreground hover:text-foreground rounded">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">
                      {t('system.public_model_id')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={draft.id}
                      onChange={e => updatePublicModelDraft({ id: e.target.value })}
                      placeholder={t('system.public_model_id_placeholder')}
                      className="w-full px-3 py-2.5 border border-border rounded-xl bg-background text-foreground text-sm font-mono"
                      autoFocus
                    />
                  </div>

                  <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-muted/40 px-3 py-2.5">
                    <span className="text-sm font-medium text-foreground">
                      {draft.enabled ? t('common:enabled') : t('common:disabled')}
                    </span>
                    <button
                      type="button"
                      onClick={() => updatePublicModelDraft({ enabled: !draft.enabled })}
                      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${draft.enabled ? 'bg-primary' : 'bg-muted'}`}
                      title={draft.enabled ? t('system.disable') : t('system.enable')}
                    >
                      <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${draft.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>

                  <div className="rounded-xl border border-border bg-muted/30 p-4">
                      <div className="space-y-4">
                        <datalist id="copilot-models-datalist-modal">
                          {copilotModels.map(model => (
                            <option key={model.id} value={model.id}>
                              {model.vendor ? `${model.name} · ${model.vendor}` : model.name}
                            </option>
                          ))}
                        </datalist>
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">{t('system.copilot_model')}</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              list="copilot-models-datalist-modal"
                              value={draft.copilotModel || ''}
                              onChange={e => updatePublicModelDraft({ copilotModel: e.target.value })}
                              placeholder={t('system.copilot_model_placeholder')}
                              className="flex-1 min-w-0 px-3 py-2.5 border border-border rounded-xl bg-background text-foreground text-sm font-mono"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setBrowseModelForUuid(draft.uuid);
                                setModelSearch('');
                                setModelVendorFilter('');
                                if (copilotModels.length === 0) loadCopilotModels(false);
                              }}
                              className="shrink-0 px-4 py-2.5 border border-border hover:bg-muted text-foreground rounded-xl flex items-center gap-2 text-sm font-medium transition-colors"
                            >
                              <Layers className="h-4 w-4" />
                              {t('system.browse_models')}
                            </button>
                          </div>
                          {selectedMeta && (
                            <div className="mt-3 rounded-lg border border-border bg-background/60 p-3">
                              <div className="text-sm font-medium text-foreground mb-2">{selectedMeta.name}</div>
                              <CopilotModelMetaChips model={selectedMeta} />
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">{t('system.reasoning_effort')}</label>
                            <select
                              value={draft.reasoningEffort || ''}
                              onChange={e => updatePublicModelDraft({ reasoningEffort: e.target.value })}
                              className="w-full px-3 py-2.5 border border-border rounded-xl bg-background text-foreground text-sm"
                            >
                              <option value="">{t('system.reasoning_effort_default')}</option>
                              <option value="low">{t('system.reasoning_effort_low')}</option>
                              <option value="medium">{t('system.reasoning_effort_medium')}</option>
                              <option value="high">{t('system.reasoning_effort_high')}</option>
                              <option value="xhigh">{t('system.reasoning_effort_max')}</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">{t('system.min_tier')}</label>
                            <select
                              value={draft.minTier || 'free'}
                              onChange={e => updatePublicModelDraft({ minTier: e.target.value })}
                              className="w-full px-3 py-2.5 border border-border rounded-xl bg-background text-foreground text-sm"
                            >
                              <option value="free">{t('system.tier_free')}</option>
                              <option value="plus">{t('system.tier_plus')}</option>
                              <option value="max">{t('system.tier_max')}</option>
                              <option value="ultra">{t('system.tier_ultra')}</option>
                            </select>
                          </div>
                        </div>
                      </div>
                  </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 p-5 border-t border-border bg-muted/50">
                  <button onClick={closeModal} className="w-full sm:w-auto px-4 py-2.5 text-muted-foreground hover:bg-muted rounded-xl">
                    {t('common:cancel')}
                  </button>
                  <button onClick={handleSavePublicModelDraft} className="w-full sm:w-auto px-5 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 font-medium flex items-center justify-center gap-2">
                    <Save className="h-4 w-4" />
                    {t('common:save')}
                  </button>
                </div>
              </div>
            </div>
          );
        })(),
        document.body
      )}

      {/* GitHub Copilot model browser — rich card picker */}
      {browseModelForUuid && createPortal(
        (() => {
          const browseRow = (data.aiSettings?.publicModels || []).find(m => m.uuid === browseModelForUuid)
            || (publicModelDraft?.uuid === browseModelForUuid ? publicModelDraft : undefined);
          const currentCopilotId = (browseRow?.copilotModel || '').trim();
          const q = modelSearch.trim().toLowerCase();
          const vendors = Array.from(new Set(copilotModels.map(m => m.vendor).filter((v): v is string => !!v))).sort();
          const priceOf = (m: CopilotModelOption) => (
            typeof m.outputPrice === 'number' ? m.outputPrice
              : typeof m.inputPrice === 'number' ? m.inputPrice
                : -1
          );
          const filtered = copilotModels
            .filter(m => !q || m.id.toLowerCase().includes(q) || m.name.toLowerCase().includes(q))
            .filter(m => !modelVendorFilter || m.vendor === modelVendorFilter)
            .slice()
            .sort((a, b) => priceOf(b) - priceOf(a));
          return (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
              onClick={() => setBrowseModelForUuid(null)}
            >
              <div
                className="bg-card rounded-xl shadow-lg w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
                onClick={e => e.stopPropagation()}
              >
                <div className="p-5 border-b border-border flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-foreground">{t('system.model_browser_title')}</h3>
                    <p className="text-sm text-muted-foreground mt-0.5">{t('system.model_browser_desc')}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => loadCopilotModels(true, true)}
                      disabled={loadingCopilotModels}
                      className="px-3 py-2 border border-border hover:bg-muted text-foreground rounded-lg flex items-center gap-2 text-sm font-medium disabled:opacity-60"
                    >
                      <RefreshCw className={`h-4 w-4 ${loadingCopilotModels ? 'animate-spin' : ''}`} />
                      {t('system.refresh_copilot_models')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setBrowseModelForUuid(null)}
                      className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="p-4 border-b border-border flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    value={modelSearch}
                    onChange={e => setModelSearch(e.target.value)}
                    placeholder={t('system.search_models_placeholder')}
                    className="flex-1 min-w-0 px-3 py-2.5 border border-border rounded-xl bg-background text-foreground text-sm"
                    autoFocus
                  />
                  <select
                    value={modelVendorFilter}
                    onChange={e => setModelVendorFilter(e.target.value)}
                    className="sm:w-48 px-3 py-2.5 border border-border rounded-xl bg-background text-foreground text-sm"
                  >
                    <option value="">{t('system.all_vendors')}</option>
                    {vendors.map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {filtered.length === 0 ? (
                    <div className="py-12 text-center text-muted-foreground text-sm">
                      {loadingCopilotModels ? t('system.refresh_copilot_models') : t('system.model_browser_empty')}
                    </div>
                  ) : (
                    filtered.map(model => {
                      const isSelected = model.id === currentCopilotId;
                      return (
                        <button
                          key={model.id}
                          type="button"
                          onClick={() => {
                            applyCopilotSelection(browseModelForUuid, model.id);
                            setBrowseModelForUuid(null);
                          }}
                          className={`w-full text-left rounded-xl border p-4 transition-colors ${
                            isSelected
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary hover:bg-muted/40'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-foreground truncate">{model.name}</div>
                              <div className="text-xs text-muted-foreground font-mono truncate">{model.id}</div>
                            </div>
                            {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
                          </div>
                          <CopilotModelMetaChips model={model} />
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          );
        })(),
        document.body
      )}
    </div>
  );
}

export default DashboardSystemParameters;
